import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RotateCcw, ShieldCheck } from "lucide-react";

const pair = (a) => (Array.isArray(a) ? `${a[0]} / ${a[1]}` : "—");
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export const RestoreBonusSpPanel = () => {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [excluded, setExcluded] = useState(new Set());
  const [confirm, setConfirm] = useState("");
  const [result, setResult] = useState(null);

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const body = dryRun ? { dry_run: true } : { dry_run: false, confirm, exclude: [...excluded] };
      const { data: res } = await api.post("/superadmin/restore-bonus-sp", body, { timeout: 180000 });
      if (dryRun) { setData(res); setResult(null); }
      else { setResult(res); setData(null); setConfirm(""); toast.success(`Restored bonus SP for ${res.applied || 0} IBAs`); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (code) => setExcluded((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n; });
  const pending = (data?.rows || []).filter((r) => !r.already_restored && (r.wiped_left_sp || r.wiped_right_sp));
  const chosen = pending.filter((r) => !excluded.has(r.user_code));
  const dueRs = chosen.reduce((s, r) => s + Number(r.bonus_due_after_rs || 0), 0);

  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-sm p-5 mb-6" data-testid="sa-restore-bonus-panel">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 grid place-items-center bg-amber-100 border border-amber-300 rounded-sm shrink-0">
          <RotateCcw size={16} className="text-amber-800" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-serif text-lg text-royal">Restore Bonus SP (wiped on 05 Oct 2026)</div>
          <p className="text-xs text-navy/60 mt-1 leading-relaxed">
            Bonus SP (1:2) is a carry-forward and must never reset at month start. The 05 Oct reset wiped it for some IBAs.
            Run the dry run, check every line, untick anyone to skip, then type <strong>RESTORE</strong> to commit.
            Pair SP and payments are not touched. Each IBA can be restored only once. Audit-logged.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={() => run(true)} disabled={busy} data-testid="sa-restore-dry-run"
              className="inline-flex items-center gap-2 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-sm disabled:opacity-50">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Run Dry Run
            </button>
          </div>
          {data ? <RestoreTable data={data} pending={pending} excluded={excluded} toggle={toggle} /> : null}
          {data && chosen.length ? (
            <div className="mt-3 bg-white border border-amber-300 rounded-sm p-3 space-y-2" data-testid="sa-restore-commit-box">
              <div className="text-xs text-navy/80" data-testid="sa-restore-selected-summary">
                Will restore <strong>{chosen.length}</strong> IBAs · bonus due at next closing after restore: <strong>{inr(dueRs)}</strong>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type RESTORE"
                  data-testid="sa-restore-confirm-input"
                  className="border border-border rounded-sm px-2 py-1.5 text-xs font-mono uppercase w-40 focus:border-gold outline-none" />
                <button onClick={() => run(false)} disabled={busy || confirm !== "RESTORE"} data-testid="sa-restore-commit"
                  className="inline-flex items-center gap-2 px-3 py-2 bg-red-700 hover:bg-red-800 text-white text-xs font-medium rounded-sm disabled:opacity-40">
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />} Commit Restore
                </button>
              </div>
            </div>
          ) : null}
          {result ? (
            <div className="mt-3 text-[11px] font-mono bg-emerald-50 border border-emerald-200 rounded-sm p-3" data-testid="sa-restore-result">
              Restored {result.applied} IBAs · +{result.total_left_sp} L / +{result.total_right_sp} R SP · audit {result.audit_log_id}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const RestoreTable = ({ data, pending, excluded, toggle }) => (
  <div className="mt-3" data-testid="sa-restore-dry-run-result">
    <div className="text-[11px] text-navy/70 mb-2" data-testid="sa-restore-summary">
      {data.ibas_listed} IBAs were reset · {pending.length} still to restore · +{data.total_left_sp} L / +{data.total_right_sp} R SP
    </div>
    <div className="overflow-x-auto max-h-[420px] overflow-y-auto border border-amber-200 rounded-sm bg-white">
      <table className="w-full text-[11px]">
        <thead className="bg-amber-100/60 sticky top-0">
          <tr>
            {["Restore", "IBA", "Wiped L / R", "Bonus SP now", "Bonus SP after", "Used by paid levels", "Carry-forward after", "Level paid", "Due at next closing"].map((h) => (
              <th key={h} className="text-left px-2 py-1.5 text-navy/60 uppercase tracking-wider font-medium whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => {
            const canPick = !r.already_restored && (r.wiped_left_sp || r.wiped_right_sp);
            return (
              <tr key={r.user_code} className="border-b border-slate-100" data-testid={`sa-restore-row-${r.user_code}`}>
                <td className="px-2 py-1">
                  {canPick ? (
                    <input type="checkbox" checked={!excluded.has(r.user_code)} onChange={() => toggle(r.user_code)}
                      data-testid={`sa-restore-check-${r.user_code}`} />
                  ) : <span className="text-emerald-700">{r.already_restored ? "done" : "—"}</span>}
                </td>
                <td className="px-2 py-1 whitespace-nowrap"><span className="font-mono">{r.user_code}</span> {r.name}{r.active ? "" : <span className="text-navy/40"> (inactive)</span>}</td>
                <td className="px-2 py-1 tabular-nums">{r.wiped_left_sp} / {r.wiped_right_sp}</td>
                <td className="px-2 py-1 tabular-nums">{pair(r.bonus_sp_now)}</td>
                <td className="px-2 py-1 tabular-nums font-semibold">{pair(r.bonus_sp_after)}</td>
                <td className="px-2 py-1 tabular-nums">{pair(r.used_by_paid_levels)}</td>
                <td className="px-2 py-1 tabular-nums font-semibold text-emerald-800">{pair(r.carry_after)}</td>
                <td className="px-2 py-1">{r.level_paid ? `Level ${r.level_paid}` : "—"}</td>
                <td className="px-2 py-1 whitespace-nowrap" data-testid={`sa-restore-due-${r.user_code}`}>
                  {r.bonus_due_after_rs > 0 ? <strong className="text-red-700">{r.levels_due_after.map((t) => `L${t}`).join(" + ")} · {inr(r.bonus_due_after_rs)}</strong> : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);
